import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Handle,
  Position,
  useUpdateNodeInternals,
  useReactFlow,
} from '@xyflow/react';

import {
  Card,
  CardContent,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  CardHeader,
  Typography,
  Box,
  TextField,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import PropTypes from 'prop-types';
import ArrowDropDownCircleOutlinedIcon from '@mui/icons-material/ArrowDropDownCircleOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Search, Close } from '@mui/icons-material';

// import BankIcon from '../images/bank.png';
// import CreditCardIcon from '../images/creditcard.png';
// import LoanIcon from '../images/loan.png';
// import FinanceIcon from '../images/finance-icon.png';
// import DestinationIcon from '../images/target-icon.png';
import { theme } from '../../style/theme';
import { useDebounce } from './../../hooks/useDebounce';
import { useParams } from 'react-router-dom';
import RefreshIcon from '@mui/icons-material/Refresh';
// import { fetchResolvedFields } from '../../state/slices/mappingSlice';
import { CONNECTOR_THEMES } from './connectorThemes';
import { resolveFieldsService } from '../../apis/EntitiesSelection';
import { useSelector } from 'react-redux';

const buildEntityLeavesMap = (nodes = []) => {
  // returns { entityId: [leafId, leafId…] }
  const map = {};
  const walk = (n) => {
    if (n.children?.length) {
      const leaves = [];
      const collectLeaves = (c) => {
        if (c.children?.length) c.children.forEach(collectLeaves);
        else leaves.push(c.id);
      };
      n.children.forEach(collectLeaves);
      map[n.id] = leaves;
      n.children.forEach(walk);
    }
  };
  nodes.forEach(walk);
  return map;
};

const DestinationNode = ({ id, data }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(true);
  const debouncedQuery = useDebounce(searchQuery, 500);

  const [hoverEntity, setHoverEntity] = useState(false);

  const [fieldRefreshLoading, setFieldRefreshLoading] = useState(false);

  const [openNodes, setOpenNodes] = useState(() => {
    const initial = {};
    const init = (nodes) => {
      nodes?.forEach((n) => {
        if (n.children) {
          initial[n.id] = true;
          init(n.children);
        }
      });
    };
    init(data?.subFields);
    return initial;
  });

  const { workflowName: workflowId } = useParams();

  const finalConfigPayload = useSelector(
    (state) => state.mapping.finalConfigPayloadByWorkflow?.[workflowId],
  );

  //eslist-disable-next-line
  const expandAllTrigger = useSelector(
    (state) => state.mapping?.expandAllTriggerByWorkflow?.[workflowId],
  );

  const AUTO_COLLAPSE_MS = 100;
  const didAutoCollapseRef = useRef(false);
  const firstRenderRef = useRef(true);

  const updateNodeInternals = useUpdateNodeInternals();
  // eslint-disable-next-line
  const { setEdges, getEdges } = useReactFlow();

  // const connectedFieldIds = useMemo(() => {
  //   return getEdges().filter(e => e.target === id).map(e => e.targetHandle);
  // }, [getEdges(), id]);

  const connectedFieldIds = getEdges()
    .filter((e) => e.target === id)
    .map((e) => e.targetHandle);

  // const nodes = getNodes();

  // console.log('nodes::::', nodes);

  // const sourceNode = useMemo(
  //   () => nodes.find((n) => n.type === 'sourceNode'),
  //   [nodes]
  // );

  // console.log('sourceNode:::', sourceNode);

  // const allEdges = getEdges();
  // const destToSourceMap = useMemo(() => {
  //   const map = {};
  //   for (const e of allEdges) {
  //     if (e.target !== id) continue;
  //     const destId = e.targetHandle ?? e.data?._prev;
  //     const srcId = e.sourceHandle ?? e.data?._originalSourceHandle;
  //     if (destId && srcId) map[destId] = srcId;
  //   }
  //   return map;
  // }, [allEdges, id]);

  // const sourceIndexMap = useMemo(() => {
  //   if (!sourceNode?.data?.subFields) return {};
  //   const flatten = (nodes, out = []) => {
  //     nodes?.forEach((n) => {
  //       if (n.children?.length) flatten(n.children, out);
  //       else out.push(n.id);
  //     });
  //     return out;
  //   };
  //   const leaves = flatten(sourceNode.data.subFields, []);
  //   const map = {};
  //   leaves.forEach((fid, idx) => (map[fid] = idx));
  //   return map;
  // }, [sourceNode]);

  // const connectedFieldIds = useMemo(
  //   () =>
  //     allEdges
  //       .filter((e) => e.target === id)
  //       .map((e) => e.targetHandle ?? e.data?._prev)
  //       .filter(Boolean),
  //   [allEdges, id]
  // );

  useEffect(() => {
    requestAnimationFrame(() => updateNodeInternals(id));
  }, [connectedFieldIds.length, JSON.stringify(data.subFields), openNodes]);

  //   useEffect(() => {
  //   const currentFieldIds = new Set();
  //   const collectFieldIds = (nodes) => {
  //     nodes?.forEach((n) => {
  //       if (n.children?.length) collectFieldIds(n.children);
  //       else currentFieldIds.add(n.id);
  //     });
  //   };
  //   collectFieldIds(data?.subFields);

  //   setEdges((eds) =>
  //     eds.filter((e) => {
  //       if (e.target !== id) return true;
  //       const targetHandle = e.targetHandle ?? e.data?._prev;
  //       // ❌ Remove if no longer valid
  //       return currentFieldIds.has(targetHandle);
  //     })
  //   );
  // }, [data?.subFields, allEdges, id]);

  const entityLeavesMap = useMemo(
    () => buildEntityLeavesMap(data?.subFields),
    [data?.subFields],
  );

  const rerouteEdgesToEntity = (entityId) => {
    const leafIds = entityLeavesMap[entityId] || [];
    if (!leafIds.length) return;
    setEdges((eds) =>
      eds.map((e) =>
        e.target === id && leafIds.includes(e.targetHandle)
          ? {
              ...e,
              targetHandle: entityId,
              data: { ...e.data, _prev: e.targetHandle },
            }
          : e,
      ),
    );
  };

  const restoreEdgesFromEntity = (entityId) => {
    setEdges((eds) =>
      eds.map((e) => {
        if (e.target === id && e.targetHandle === entityId && e.data?._prev) {
          const { _prev, ...rest } = e.data;
          return { ...e, targetHandle: _prev, data: rest };
        }
        return e;
      }),
    );
  };

  const toggleNode = (entityId) => {
    const willCollapse = openNodes[entityId];
    if (willCollapse) rerouteEdgesToEntity(entityId);
    else restoreEdgesFromEntity(entityId);

    setOpenNodes((p) => ({ ...p, [entityId]: !p[entityId] }));
    requestAnimationFrame(() => updateNodeInternals(id));
  };

  useEffect(() => {
    if (didAutoCollapseRef.current) return;

    const init = {};
    const walk = (nodes) => {
      nodes?.forEach((n) => {
        if (n.children?.length) {
          init[n.id] = true;
          walk(n.children);
        }
      });
    };
    walk(data?.subFields);
    setOpenNodes(init);

    const t = setTimeout(() => {
      // Batch all edge rerouting in a single setEdges call to ensure atomic update
      setEdges((eds) => {
        const entityIdsToReroute = [];
        data?.subFields?.forEach((n) => {
          if (n.children?.length) {
            entityIdsToReroute.push(n.id);
          }
        });

        return eds.map((e) => {
          if (e.target !== id) return e;

          // Check if this edge should be rerouted to any entity
          for (const entityId of entityIdsToReroute) {
            const leafIds = entityLeavesMap[entityId] || [];
            if (leafIds.includes(e.targetHandle)) {
              return {
                ...e,
                targetHandle: entityId,
                data: { ...e.data, _prev: e.targetHandle },
              };
            }
          }
          return e;
        });
      });

      // Then update node state
      setOpenNodes((prev) => {
        const next = { ...prev };
        data?.subFields?.forEach((n) => {
          if (n.children?.length) {
            next[n.id] = false;
          }
        });
        return next;
      });

      didAutoCollapseRef.current = true;

      // Use double requestAnimationFrame to ensure DOM is ready and edges are properly positioned
      // This ensures edges are fully updated in React Flow before updating node internals
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          updateNodeInternals(id);
        });
      });
    }, AUTO_COLLAPSE_MS);

    return () => clearTimeout(t);
  }, [data?.subFields, id, entityLeavesMap, setEdges, updateNodeInternals]);

  useEffect(() => {
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      return;
    }
    if (!expandAllTrigger) return;

    const allOpen = {};
    const walk = (nodes) => {
      nodes?.forEach((n) => {
        if (n.children) {
          allOpen[n.id] = true;
          walk(n.children);
        }
      });
    };
    walk(data?.subFields);
    setOpenNodes(allOpen);
    Object.keys(entityLeavesMap).forEach(restoreEdgesFromEntity);
    requestAnimationFrame(() => updateNodeInternals(id));
  }, [expandAllTrigger]);

  const filterTree = (nodes, q) => {
    if (!q) return nodes;
    const words = q.toLowerCase().split(/\s+/).filter(Boolean);
    const connectedSet = new Set(connectedFieldIds);

    const filterNode = (n) => {
      const labelMatches =
        words.length === 0 ||
        words.every((w) => n.label.toLowerCase().includes(w));
      const isConnected = connectedSet.has(n.id);

      if (n.children) {
        const kids = n.children.map(filterNode).filter(Boolean);
        return labelMatches || isConnected || kids.length
          ? { ...n, children: kids }
          : null;
      }

      return labelMatches || isConnected ? n : null;
    };

    return nodes.map(filterNode).filter(Boolean);
  };

  const getFieldStatus = (fieldId, side) => {
    const edges = getEdges().filter((e) =>
      side === 'source'
        ? e.sourceHandle === fieldId
        : e.targetHandle === fieldId,
    );

    const hasManual = edges.some((e) => e.data?.isManual);
    const hasFuzzyValid = edges.some((e) => e.data?.isFuzzy && e.data?.isValid);
    const hasInvalid = edges.some((e) => e.data?.isValid === false);

    return {
      hasManual,
      hasFuzzyValid,
      hasInvalid,
      isConnected: edges.length > 0,
    };
  };

  const renderTree = (nodes, lvl = 0) => {
    if (!Array.isArray(nodes)) return null;
    return (
      <List component="div" disablePadding>
        {nodes.map((n, idx) => {
          const isBranch = !!n.children;

          // let children = n.children || [];
          // children = [
          //   ...children.filter((f) => connectedFieldIds.includes(f.id)),
          //   ...children.filter((f) => !connectedFieldIds.includes(f.id)),
          // ];

          // const isField = !isBranch;
          // const isConnected = isField && connectedFieldIds.includes(n.id);

          // const { hasFuzzyValid} = getFieldStatus(
          //   n.id,
          //   'target'
          // );

          // let children = n.children || [];
          // if (children.length) {
          //   const connected = children.filter((f) =>
          //     connectedFieldIds.includes(f.id)
          //   );
          //   const unconnected = children.filter(
          //     (f) => !connectedFieldIds.includes(f.id)
          //   );

          //   connected.sort((a, b) => {
          //     const srcA = destToSourceMap[a.id];
          //     const srcB = destToSourceMap[b.id];
          //     const idxA =
          //       srcA in sourceIndexMap
          //         ? sourceIndexMap[srcA]
          //         : Number.MAX_SAFE_INTEGER;
          //     const idxB =
          //       srcB in sourceIndexMap
          //         ? sourceIndexMap[srcB]
          //         : Number.MAX_SAFE_INTEGER;
          //     return idxA - idxB;
          //   });

          //   children = [...connected, ...unconnected];
          // }

          // if (!isBranch) {
          //   console.log('Destination field object:', n);
          // }
          let children = n.children || [];
          children = [
            ...children.filter((f) => connectedFieldIds.includes(f.id)),
            ...children.filter((f) => !connectedFieldIds.includes(f.id)),
          ];

          const isField = !isBranch;
          const isConnected = isField && connectedFieldIds.includes(n.id);

          const { hasFuzzyValid } = getFieldStatus(n.id, 'target');

          // let children = n.children || [];
          // if (children.length) {
          //   const connected = children.filter((f) =>
          //     connectedFieldIds.includes(f.id)
          //   );
          //   const unconnected = children.filter(
          //     (f) => !connectedFieldIds.includes(f.id)
          //   );

          //   connected.sort((a, b) => {
          //     const srcA = destToSourceMap[a.id];
          //     const srcB = destToSourceMap[b.id];
          //     const idxA =
          //       srcA in sourceIndexMap
          //         ? sourceIndexMap[srcA]
          //         : Number.MAX_SAFE_INTEGER;
          //     const idxB =
          //       srcB in sourceIndexMap
          //         ? sourceIndexMap[srcB]
          //         : Number.MAX_SAFE_INTEGER;
          //     return idxA - idxB;
          //   });

          //   children = [...connected, ...unconnected];
          // }

          // if (!isBranch) {
          //   console.log('Destination field object:', n);
          // }

          // const isDisabled =
          //   isField && !n.is_mandatory && n.is_creatable === false;

          const isDisabled = isField && n.is_creatable === false;

          // const showStar =
          //   isField && n.is_mandatory === true && n.is_creatable === true;

          const showStarRed =
            isField && n.is_mandatory === true && n.is_creatable === true;
          const showStarGrey =
            isField && n.is_mandatory === true && n.is_creatable === false;

          const showHandle =
            isField &&
            ((n.is_mandatory && n.is_creatable === true) ||
              (!n.is_mandatory && n.is_creatable === true));

          return (
            <div key={n.id}>
              <ListItemButton
                onClick={() => isBranch && toggleNode(n.id)}
                disabled={isDisabled}
                sx={{
                  ...(isDisabled && {
                    background: '#f8f8f8',
                    pointerEvents: 'none',
                  }),
                  my: 1,
                  '&:hover': { backgroundColor: 'transparent' },
                  ...(isBranch && {
                    bgcolor: lvl === 0 ? '#E5EFFB' : '#F2F2F2',
                    border:
                      lvl === 0 ? '1px solid #264FDA' : '1px solid #000000',
                    borderRadius: 1,
                    color: '#264FDA',
                    py: 0.5,
                    position: 'relative',
                  }),
                  ...(!isBranch && {
                    borderBottom:
                      idx === nodes.length - 1
                        ? 'none'
                        : '0.1px solid rgba(56,56,56,.2)',
                    pb: 0,
                    pt: 0.5,
                    pl: 2,
                  }),
                }}
              >
                <ListItemText
                  primary={
                    <span
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        gap: 4,
                        color: ['Bank', 'Credit Card', 'Loan'].includes(n.label)
                          ? '#000'
                          : undefined,
                      }}
                    >
                      {/* Label with color and wrapping */}
                      <span
                        style={{
                          display: 'block',
                          color:
                            (hasFuzzyValid === true && isConnected) ||
                            (n.color === true && isConnected)
                              ? '#006400'
                              : n.isMismatch
                                ? '#e60000'
                                : 'black',
                          fontWeight:
                            (hasFuzzyValid === true && isConnected) ||
                            (n.color === true && isConnected)
                              ? '550'
                              : 'normal',
                          wordBreak: 'break-word',
                          whiteSpace: 'normal',
                          textAlign: 'left',
                          paddingLeft: '3.5rem',
                        }}
                      >
                        {n.label}
                        {showStarRed && (
                          <span
                            style={{
                              color: '#d32f2f',
                              marginLeft: 4,
                              fontWeight: 500,
                              fontSize: '1.4rem',
                              verticalAlign: 'middle',
                              cursor: 'help',
                            }}
                            title="Mandatory: This field must be mapped."
                          >
                            *
                          </span>
                        )}

                        {showStarGrey && (
                          <span
                            title="This field is system managed or read only."
                            style={{
                              marginLeft: 4,
                              fontWeight: 500,
                              fontSize: '1.4rem',
                              color: '#d32f2f',
                              verticalAlign: 'middle',
                              cursor: 'help',
                              pointerEvents: 'auto',
                            }}
                          >
                            *
                          </span>
                        )}

                        <span
                          style={{
                            fontSize: 12.5,
                            fontWeight: 'normal',
                            color: '#636363',
                          }}
                        >
                          &nbsp;&nbsp;&nbsp;&nbsp;
                          {n.type ? `${n.type}` : ''}
                        </span>
                      </span>
                    </span>
                  }
                />

                {isBranch && (
                  <ListItemIcon sx={{ minWidth: 20 }}>
                    {openNodes[n.id] ? (
                      <RemoveCircleOutlineIcon
                        sx={{
                          color: ['Bank', 'Credit Card', 'Loan'].includes(
                            n.label,
                          )
                            ? '#A0A0A0'
                            : '#264FDA',
                          ml: 1,
                        }}
                      />
                    ) : (
                      <ArrowDropDownCircleOutlinedIcon
                        sx={{
                          color: ['Bank', 'Credit Card', 'Loan'].includes(
                            n.label,
                          )
                            ? '#A0A0A0'
                            : '#264FDA',
                          ml: 1,
                        }}
                      />
                    )}
                  </ListItemIcon>
                )}

                {isBranch && (
                  <Handle
                    type="target"
                    position={Position.Left}
                    id={n.id}
                    onMouseEnter={() => setHoverEntity(true)}
                    onMouseLeave={() => setHoverEntity(false)}
                    style={{
                      background: hoverEntity ? 'transparent' : '#E5EFFB',
                      border: 'none',
                      boxShadow: 'none',
                      opacity: hoverEntity ? 1 : 0,
                      // left: '11%',
                      left: '0',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      position: 'absolute',
                      // display: openNodes[n.id] ? 'none' : 'block',
                      display: 'block',
                    }}
                  />
                )}

                {!isBranch && showHandle && (
                  <Handle
                    type="target"
                    position={Position.Left}
                    id={n.id}
                    style={{
                      background: '#02296C',
                      left: '11%',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      position: 'absolute',
                    }}
                  />
                )}
              </ListItemButton>

              {isBranch && (
                <Collapse in={!!openNodes[n.id]} timeout="auto">
                  {renderTree(children, lvl + 1)}
                </Collapse>
              )}
            </div>
          );
        })}
      </List>
    );
  };

  // const handleNodeFieldRefresh = () => {
  //   if (!workflowName || !finalConfigPayload?.length) return;
  //   dispatch(fetchResolvedFields({
  //     label: 'FieldResolution',
  //     workflowName,
  //     finalConfigPayload,
  //     force: true,
  //   }));
  // };
  const getOnlySourceEntities = (configList = []) =>
    configList.filter((item) => item.type === 'destination');

  // console.log('getOnlySourceEntities', getOnlySourceEntities(finalConfigPayload));
  const handleNodeFieldRefresh = async () => {
    if (!workflowId || !finalConfigPayload?.length) return;

    const sourceOnlyPayload = getOnlySourceEntities(finalConfigPayload);

    try {
      setFieldRefreshLoading(true); // Start loader

      const response = await resolveFieldsService({
        label: 'FieldResolution',
        configs: sourceOnlyPayload,
        force: true,
      });

      console.log('Resolved Fields Response:', response);
      // Optionally: dispatch(setResolvedFields(response));
      //     dispatch(setResolvedFields({
      //   workflowName,
      //   resolvedFields: response,
      // }));
    } catch (error) {
      console.error('Error resolving fields:', error);
    } finally {
      setFieldRefreshLoading(false);
    }
  };

  const nodeConfig = finalConfigPayload?.find((cfg) => cfg.type === data.type);
  const labelPrefix = nodeConfig?.label?.split('(')[0].trim().toLowerCase();
  const connectorTheme =
    CONNECTOR_THEMES[labelPrefix] || CONNECTOR_THEMES.default;

  return (
    <Card sx={{ width: 400, border: '1px solid #C7C7C7', borderRadius: 2 }}>
      <CardHeader
        // avatar={<img src={DestinationIcon} alt="" width={26} />}
        // avatar={
        //   connectorTheme.logo && (
        //     <img
        //       src={connectorTheme.logo}
        //       alt={connectorTheme.label}
        //       width={26}
        //     />
        //   )
        // }
        title={
          <Typography variant="subtitle1" fontWeight={500}>
            Destination - {connectorTheme.label}
          </Typography>
        }
        action={
          <>
            <Tooltip title="Reload field data">
              <span>
                <IconButton
                  size="small"
                  onClick={handleNodeFieldRefresh}
                  disabled={fieldRefreshLoading}
                  aria-label="Reload field data"
                  sx={{ color: '#fff' }}
                >
                  {fieldRefreshLoading ? (
                    <CircularProgress size={18} />
                  ) : (
                    <RefreshIcon />
                  )}
                </IconButton>
              </span>
            </Tooltip>

            <IconButton
              sx={{ color: '#fff' }}
              onClick={() => setShowSearch(!showSearch)}
            >
              <Search />
            </IconButton>
          </>
        }
        sx={{
          // background: connectorTheme.gradient,
          background: '#383838',
          color: '#fff',
          px: 2,
          py: 1,
          boxShadow: '0 2px 8px rgba(107, 114, 128, 0.3)',
        }}
      />

      <CardContent sx={{ pt: 1 }}>
        <Collapse in={showSearch} timeout="auto" unmountOnExit>
          <Box>
            <TextField
              placeholder="Search Fields…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              fullWidth
              size="small"
              variant="outlined"
              sx={{
                backgroundColor: '#fff',
                borderRadius: 1,
                '& .MuiOutlinedInput-root': { fontSize: 16, py: 0.5 },
              }}
              InputProps={{
                endAdornment: searchQuery && (
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                    sx={{
                      backgroundColor: '#fff',
                      color: theme.palette.error.main,
                      width: '1.25rem',
                      height: '1.25rem',
                      borderRadius: '50%',
                      '&:hover': {
                        backgroundColor: theme.palette.error.dark,
                        color: '#fff',
                      },
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                ),
              }}
            />
          </Box>
        </Collapse>

        {renderTree(filterTree(data.subFields, debouncedQuery))}
      </CardContent>
    </Card>
  );
};

DestinationNode.propTypes = {
  id: PropTypes.string,
  data: PropTypes.shape({
    label: PropTypes.string.isRequired,
    type: PropTypes.string,
    subFields: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        type: PropTypes.string,
        children: PropTypes.array,
      }),
    ).isRequired,
  }).isRequired,
};

export default DestinationNode;
